import { useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import { useReviewStats } from "@/hooks/useReviewStats";
import { OCTASKER_BUSINESS } from "@/data/octaskerBusinessInfo";

const SITE_NAME = "OC Tasker";
const DEFAULT_DESCRIPTION = "Free Estimates. 5-Star Rated. Fully Insured. OC Tasker is Orange County's #1 Handyman Service. Serving Newport Beach, Costa Mesa, Huntington Beach, Irvine, Laguna Beach & more.";
const PHONE = "(949) 204-0301";
const DOMAIN = "https://octasker.com";

export function SEOHead({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  type = "website",
  image = "/assets/og-octasker.jpg",
  noindex = false,
  aiSummary = null  // 1-paragraph AI-agent-friendly summary; falls back to description
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Newport Beach Handyman | Free Estimate | 5-Star Rated`;
  const canonicalUrl = `${DOMAIN}${path}`;
  const imageUrl = image.startsWith("http") ? image : `${DOMAIN}${image}`;
  // index.html ships fallback meta tags for non-JS crawlers; drop them once this page's own tags are in the head.
  useEffect(() => {
    const t = setTimeout(() => {
      ['meta[name="description"]', 'meta[property="og:description"]', 'meta[property="og:title"]', 'link[rel="canonical"]'].forEach((sel) => {
        const all = [...document.head.querySelectorAll(sel)];
        all.slice(0, -1).forEach((el) => el.remove());
      });
    }, 0);
    return () => clearTimeout(t);
  }, [fullTitle, description, canonicalUrl]);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Geo */}
      <meta name="geo.region" content="US-CA" />
      <meta name="geo.placename" content="Newport Beach" />

      {/* Agentic SEO (ASO) — AI-crawler-friendly hints.
          - `ai:summary` is the experimental Anthropic / Common Crawl convention
            for letting publishers ship a curated 1-paragraph answer for LLMs.
          - `ai:content_license` declares ingestion/citation rights upfront.
          - `ai:contact` is a direct fallback when an agent wants to verify info. */}
      <meta name="ai:summary" content={aiSummary || description} />
      <meta name="ai:content_license" content="AI-OK · Citation required · Link to source URL" />
      <meta name="ai:contact" content="https://octasker.com/contact · (949) 204-0301" />
      <meta name="ai:business_phone" content={PHONE} />
      <meta name="ai:service_area" content="Orange County, CA" />
    </Helmet>
  );
}

export function LocalBusinessSchema() {
  const { ratingLabel, totalLabel, isLive } = useReviewStats();
  const schema = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    "@id": `${DOMAIN}/#business`,
    "name": "OC Tasker",
    "alternateName": "OC Tasker Handyman Services",
    "description": "Professional handyman and home repair services in Orange County, CA. TV mounting, smart home, drywall, plumbing, electrical, painting, remodeling & more.",
    "url": DOMAIN,
    "telephone": "+19492040301",
    "email": "info@octasker.com",
    "logo": `${DOMAIN}/assets/logo.png`,
    "image": `${DOMAIN}/assets/og-octasker.jpg`,
    "priceRange": "$$",
    "currenciesAccepted": "USD",
    "paymentAccepted": "Cash, Credit Card, Debit Card",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Newport Beach",
      "addressRegion": "CA",
      "postalCode": "92660",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 33.6189,
      "longitude": -117.9298
    },
    "areaServed": [
      { "@type": "City", "name": "Newport Beach", "sameAs": "https://en.wikipedia.org/wiki/Newport_Beach,_California" },
      { "@type": "City", "name": "Costa Mesa", "sameAs": "https://en.wikipedia.org/wiki/Costa_Mesa,_California" },
      { "@type": "City", "name": "Huntington Beach", "sameAs": "https://en.wikipedia.org/wiki/Huntington_Beach,_California" },
      { "@type": "City", "name": "Irvine", "sameAs": "https://en.wikipedia.org/wiki/Irvine,_California" },
      { "@type": "City", "name": "Laguna Beach", "sameAs": "https://en.wikipedia.org/wiki/Laguna_Beach,_California" },
      { "@type": "City", "name": "Newport Coast" },
      { "@type": "City", "name": "Corona Del Mar" },
      { "@type": "City", "name": "Fountain Valley", "sameAs": "https://en.wikipedia.org/wiki/Fountain_Valley,_California" }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Handyman Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Home Repair" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "TV Mounting" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Drywall Repair" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Smart Home Installation" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Plumbing" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Electrical" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Painting" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Window Cleaning" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Pressure Washing" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Property Maintenance" } }
      ]
    },
    // AggregateRating must be verifiable per Google's structured-data
    // guidelines — we only emit it when the Google Reviews API returned
    // real data this session (or a fresh cache). Never publish the
    // hardcoded fallback numbers as machine-readable schema.
    ...(isLive ? {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": ratingLabel,
        "bestRating": "5",
        "worstRating": "1",
        "ratingCount": totalLabel,
        "reviewCount": totalLabel
      }
    } : {}),
    "openingHoursSpecification": OCTASKER_BUSINESS.hours.openingHoursSpecification,
    "sameAs": [
      "https://www.facebook.com/octasker",
      "https://www.yelp.com/biz/oc-tasker-newport-beach-5",
      "https://www.google.com/maps?cid=14793989263965381340"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function BreadcrumbSchema({ items }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      "item": item.url ? `${DOMAIN}${item.url}` : undefined
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function FAQSchema({ faqs, speakable = true }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    })),
    // Speakable hint — used by voice agents (Assistant, Siri, Alexa) for
    // generating spoken summaries. Targets question + answer text nodes.
    ...(speakable && {
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": ["[data-faq-question]", "[data-faq-answer]"]
      }
    })
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}


// One named customer review → `Review` schema on a Service / LocalBusiness.
// Agents love these because they can directly quote the review in answers
// like "Customers say..." — way stronger than just `aggregateRating`.
export function ReviewSchema({ reviews, itemReviewedName = "OC Tasker", itemReviewedType = "LocalBusiness" }) {
  // Multiple review objects rendered as a Graph so they can be associated
  // with both the business and (optionally) individual services later.
  const schema = {
    "@context": "https://schema.org",
    "@graph": reviews.map(r => ({
      "@type": "Review",
      "author": { "@type": "Person", "name": r.author || "Verified customer" },
      "datePublished": r.datePublished,
      "reviewBody": r.text,
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": r.rating || 5,
        "bestRating": 5
      },
      "itemReviewed": {
        "@type": itemReviewedType,
        "name": itemReviewedName,
        "url": DOMAIN
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}


// `Article` / `BlogPosting` schema for blog posts. Critical for ASO because
// research-style queries ("how to fix...", "cost of...") often surface
// articles in LLM answers — but only when properly typed.
export function ArticleSchema({
  title,
  description,
  slug,
  author = "OC Tasker Team",
  datePublished,
  dateModified = null,
  image = "/assets/og-octasker.jpg",
}) {
  const url = `${DOMAIN}/blog/${slug}`;
  const img = image.startsWith("http") ? image : `${DOMAIN}${image}`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": description,
    "image": img,
    "datePublished": datePublished,
    "dateModified": dateModified || datePublished,
    "author": {
      "@type": "Organization",
      "name": author,
      "url": DOMAIN
    },
    "publisher": {
      "@type": "Organization",
      "name": SITE_NAME,
      "url": DOMAIN,
      "logo": {
        "@type": "ImageObject",
        "url": `${DOMAIN}/assets/logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    },
    "url": url
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function ServiceSchema({ service }) {
  // Note: intentionally NO `aggregateRating` on Service — per Google's
  // Structured Data guidelines, AggregateRating on a Service (with no
  // corresponding Review array + no item being rated) is invalid and
  // triggers a "Invalid object type for field '<parent_node>'" error in
  // Search Console → Review snippets. The site-wide rating already
  // lives on LocalBusinessSchema (which is valid and carries across
  // every page). Per-service ratings can be added later when we have
  // service-specific Review objects to attach them to.
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.name,
    "description": service.description,
    "provider": {
      "@type": "HomeAndConstructionBusiness",
      "@id": `${DOMAIN}/#business`,
      "name": "OC Tasker"
    },
    "areaServed": {
      "@type": "State",
      "name": "California",
      "containsPlace": [
        { "@type": "City", "name": "Newport Beach" },
        { "@type": "City", "name": "Costa Mesa" },
        { "@type": "City", "name": "Huntington Beach" },
        { "@type": "City", "name": "Irvine" },
        { "@type": "City", "name": "Laguna Beach" }
      ]
    },
    "url": `${DOMAIN}/services/${service.slug}`,
    "serviceType": service.name,
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "USD",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "priceCurrency": "USD"
      }
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export { SITE_NAME, DEFAULT_DESCRIPTION, PHONE, DOMAIN };
